/* eslint-disable react/prop-types */
import React from 'react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import Select from 'react-select';
import CreatableSelect from 'react-select/lib/Creatable';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

// Exported for testing
export function Control(props) {
  return (
    <TextField
      fullWidth
      error={props.selectProps.hasError}
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          'aria-label': props.selectProps.placeholder,
          ...props.innerProps,
        },
        disableUnderline: true,
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      className={props.selectProps.classes.menuItem}
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function SingleValue(props) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={props.selectProps.classes.chip}
      onDelete={event => {
        props.removeProps.onClick();
        props.removeProps.onMouseDown(event);
      }}
    />
  );
}

function Menu(props) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

function InnerOption(props) {
  // We don't receive the style classes here
  return (
    <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <span>{props.label}</span>
      {props.inactive ? (
        <span style={{ background: '#F0F1F4', borderRadius: '4px', padding: '0px 4px' }}>
          Inactive
        </span>
      ) : null}
    </span>
  );
}

const components = {
  Option,
  Control,
  NoOptionsMessage,
  Placeholder,
  SingleValue,
  MultiValue,
  ValueContainer,
  Menu,
};

class Typeahead extends React.Component {
  onInputChange = debounce((value, actionMeta) => {
    if (this.props.onInputChange) {
      this.props.onInputChange(value, actionMeta);
    }
  }, 300);

  handleChange = () => value => {
    if (this.props.onChange) {
      this.props.onChange(value);
    }
  };

  render() {
    const {
      classes,
      theme,
      suggestions,
      placeholder,
      isLoading,
      type,
      isCreatable,
      onCreateNew,
      disabled,
      hasError,
      noOptionsMessage,
      value,
    } = this.props;

    const selectStyles = {
      input: base => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
      dropdownIndicator: (base, { isDisabled }) => ({ ...base, opacity: isDisabled ? '0.2' : '1' }),
    };

    if (isCreatable) {
      return (
        <div className={classes.root}>
          <CreatableSelect
            classes={classes}
            disabled={disabled}
            styles={selectStyles}
            options={suggestions}
            components={components}
            value={value}
            onChange={this.handleChange(type)}
            onInputChange={this.onInputChange}
            placeholder={placeholder}
            isLoading={isLoading}
            formatOptionLabel={InnerOption}
            isCreatable
            onCreateOption={onCreateNew}
            hasError={hasError}
          />
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Select
          classes={classes}
          isDisabled={disabled}
          styles={selectStyles}
          options={suggestions}
          components={components}
          value={value}
          onChange={this.handleChange(type)}
          onInputChange={this.onInputChange}
          placeholder={placeholder}
          isLoading={isLoading}
          formatOptionLabel={InnerOption}
          isCreatable={isCreatable}
          onCreateOption={onCreateNew}
          hasError={hasError}
          noOptionsMessage={noOptionsMessage}
        />
      </div>
    );
  }
}

Typeahead.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  theme: PropTypes.object.isRequired,
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      inactive: PropTypes.bool,
    }),
  ).isRequired,
  placeholder: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  onInputChange: PropTypes.func,
  type: PropTypes.string,
  isCreatable: PropTypes.bool,
  onCreateNew: PropTypes.func,
  disabled: PropTypes.bool,
  hasError: PropTypes.bool,
};

Typeahead.defaultProps = {
  onChange: undefined,
  isLoading: false,
  onInputChange: undefined,
  type: 'single',
  isCreatable: false,
  onCreateNew: undefined,
  disabled: false,
  hasError: false,
  defaultValue: null,
  defaultValues: null,
};

export default Typeahead;
